import React, { useEffect, useMemo, useState } from "react";
import client, { apiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { numberFmt, dateTimeFmt } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, ArrowLeftRight, Loader2, FileText } from "lucide-react";

export default function Transfer() {
  const { user } = useAuth();
  const { branches } = useBranch();
  const isAdmin = ["super_admin", "admin"].includes(user.role);
  const isWarehouse = user.role === "warehouse";
  const lockedFrom = ["branch_head", "warehouse"].includes(user.role);
  const canCreate = isAdmin || isWarehouse;
  const [products, setProducts] = useState([]);
  const [history, setHistory] = useState([]);
  const [fromBranch, setFromBranch] = useState(lockedFrom ? user.branch_id : "");
  const [toBranch, setToBranch] = useState("");
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [busyRecvId, setBusyRecvId] = useState(null);

  const loadStock = (b) => { if (b) client.get("/products", { params: { branch_id: b } }).then((r) => setProducts(r.data)); };
  const loadHistory = () => client.get("/transfers").then((r) => setHistory(r.data));
  useEffect(() => { loadHistory(); }, []);
  useEffect(() => { loadStock(fromBranch); }, [fromBranch]);

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "-";
  const fromOptions = lockedFrom ? branches.filter((b) => b.id === user.branch_id) : branches;
  const fromMgr = branches.find((b) => b.id === fromBranch)?.manager_id;
  const toOptions = branches.filter((b) => b.id !== fromBranch && (!fromMgr || b.manager_id === fromMgr));

  const addLine = () => setLines((l) => [...l, { _k: Math.random().toString(36).slice(2), product_id: "", unit: "", qty: 1 }]);
  const setLine = (i, patch) => setLines((l) => l.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const delLine = (i) => setLines((l) => l.filter((_, idx) => idx !== i));
  const prodUnits = (pid) => products.find((p) => p.id === pid)?.units || [];
  const prodStock = (pid) => products.find((p) => p.id === pid)?.stock_qty ?? 0;

  const submit = async () => {
    if (saving) return;
    if (!fromBranch || !toBranch) { toast.error("Pilih cabang asal & tujuan"); return; }
    if (!lines.length) { toast.error("Tambah minimal 1 item"); return; }
    setSaving(true);
    try {
      await client.post("/transfers", {
        from_branch: fromBranch, to_branch: toBranch,
        items: lines.map((l) => ({ product_id: l.product_id, unit: l.unit, qty: Number(l.qty) })),
      });
      toast.success("Transfer dikirim — menunggu konfirmasi cabang tujuan");
      setLines([]); loadStock(fromBranch); loadHistory();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const receive = async (t) => {
    if (busyRecvId) return;
    setBusyRecvId(t.id);
    try {
      await client.post(`/transfers/${t.id}/receive`);
      toast.success("Transfer diterima — stok cabang tujuan bertambah");
      loadHistory();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusyRecvId(null); }
  };

  const canReceive = (t) => t.status === "shipped" && (["super_admin", "admin"].includes(user.role) || user.branch_id === t.to_branch);

  const printTransfer = (t) => {
    const rows = t.items.map((i) => `<tr><td style="padding:4px 8px;border-bottom:1px solid #eee">${i.name}</td><td style="padding:4px 8px;border-bottom:1px solid #eee;text-align:right">${i.qty} ${i.unit}</td></tr>`).join("");
    const w = window.open("", "_blank", "width=720,height=800");
    w.document.write(`<html><head><title>${t.number}</title></head><body style="font-family:Arial;padding:28px;color:#0f172a">
      <h2 style="margin:0">TOKO KU — Surat Jalan / Transfer Barang</h2>
      <div style="font-size:13px;margin:6px 0 14px">No: <b>${t.number}</b> · ${new Date(t.created_at).toLocaleString("id-ID")}</div>
      <table style="font-size:13px;margin-bottom:14px"><tr><td style="padding-right:24px">Dari: <b>${branchName(t.from_branch)}</b></td><td>Ke: <b>${branchName(t.to_branch)}</b></td></tr>
      <tr><td>Status: <b>${t.status === "received" ? "Diterima" : "Dikirim"}</b></td><td>Oleh: ${t.user_name || "-"}</td></tr></table>
      <table style="width:100%;border-collapse:collapse;font-size:13px"><thead><tr style="background:#f1f5f9"><th style="padding:6px 8px;text-align:left">Produk</th><th style="padding:6px 8px;text-align:right">Qty</th></tr></thead><tbody>${rows}</tbody></table>
      <div style="margin-top:40px;display:flex;justify-content:space-between;font-size:13px"><div>Pengirim<br/><br/><br/>(__________)</div><div>Penerima<br/><br/><br/>(__________)</div></div>
      </body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };

  return (
    <div className="p-6 space-y-6 animate-in-up">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Transfer Antar Cabang</h1>
        <p className="text-sm text-muted-foreground">{isWarehouse ? "Transfer stok dari Gudang Utama ke cabang di region Anda" : isAdmin ? "Pindahkan stok antar cabang — hanya Regional Manager yang dapat membuat transfer" : "Konfirmasi penerimaan stok transfer masuk ke cabang Anda"}</p>
      </div>

      {canCreate && (
      <Card className="p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2"><Label>Cabang Asal</Label>
            <Select value={fromBranch} onValueChange={(v) => { setFromBranch(v); if (v === toBranch) setToBranch(""); }} disabled={lockedFrom}>
              <SelectTrigger data-testid="transfer-from"><SelectValue placeholder="Pilih cabang asal" /></SelectTrigger>
              <SelectContent>{fromOptions.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Cabang Tujuan</Label>
            <Select value={toBranch} onValueChange={setToBranch}>
              <SelectTrigger data-testid="transfer-to"><SelectValue placeholder="Pilih cabang tujuan" /></SelectTrigger>
              <SelectContent>{toOptions.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between">
            <Label>Item Transfer</Label>
            <Button size="sm" variant="outline" onClick={addLine} disabled={!fromBranch} data-testid="transfer-add-line"><Plus className="mr-1 h-3 w-3" /> Tambah Item</Button>
          </div>
          {lines.map((l, i) => (
            <div key={l._k} className="grid grid-cols-12 items-center gap-2" data-testid={`transfer-line-${i}`}>
              <div className="col-span-6">
                <Select value={l.product_id} onValueChange={(v) => setLine(i, { product_id: v, unit: products.find((p) => p.id === v)?.base_unit || "" })}>
                  <SelectTrigger data-testid={`transfer-product-${i}`}><SelectValue placeholder="Produk" /></SelectTrigger>
                  <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} (stok {numberFmt(p.stock_qty)})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-3">
                <Select value={l.unit} onValueChange={(v) => setLine(i, { unit: v })}>
                  <SelectTrigger data-testid={`transfer-unit-${i}`}><SelectValue placeholder="Satuan" /></SelectTrigger>
                  <SelectContent>{prodUnits(l.product_id).map((u) => <SelectItem key={u.name} value={u.name}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Input className="col-span-2 font-mono" type="number" placeholder="Qty" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} data-testid={`transfer-qty-${i}`} />
              <button className="col-span-1 flex justify-center text-muted-foreground hover:text-destructive" onClick={() => delLine(i)}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {!lines.length && <div className="rounded-md border border-dashed py-8 text-center text-sm text-muted-foreground">Belum ada item.</div>}
        </div>

        <div className="mt-4 flex justify-end border-t pt-4">
          <Button onClick={submit} disabled={saving} data-testid="submit-transfer">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowLeftRight className="mr-2 h-4 w-4" />} {saving ? "Memproses…" : "Proses Transfer"}
          </Button>
        </div>
      </Card>
      )}

      <div>
        <h2 className="mb-3 font-heading text-xl font-semibold">Riwayat Transfer</h2>
        <div className="rounded-lg border bg-white">
          <Table>
            <TableHeader><TableRow><TableHead>No</TableHead><TableHead>Waktu</TableHead><TableHead>Rute</TableHead><TableHead>Item</TableHead><TableHead>Status</TableHead><TableHead>Oleh</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {history.map((t) => (
                <TableRow key={t.id} className="hover:bg-slate-50" data-testid={`transfer-hist-${t.id}`}>
                  <TableCell className="font-mono text-xs">{t.number}</TableCell>
                  <TableCell className="text-xs">{dateTimeFmt(t.created_at)}</TableCell>
                  <TableCell className="text-sm"><Badge variant="secondary">{branchName(t.from_branch)}</Badge> <ArrowLeftRight className="inline h-3 w-3" /> <Badge variant="secondary">{branchName(t.to_branch)}</Badge></TableCell>
                  <TableCell className="text-sm">{t.items.length} item</TableCell>
                  <TableCell>
                    {t.status === "received"
                      ? <Badge className="bg-emerald-100 text-emerald-700">Diterima</Badge>
                      : <Badge className="bg-amber-100 text-amber-700">Dikirim</Badge>}
                  </TableCell>
                  <TableCell className="text-sm">{t.user_name}</TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => printTransfer(t)} data-testid={`pdf-${t.id}`}><FileText className="mr-1 h-3 w-3" /> PDF</Button>
                      {canReceive(t) && <Button size="sm" onClick={() => receive(t)} disabled={busyRecvId === t.id} data-testid={`receive-${t.id}`}>{busyRecvId === t.id ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Memproses…</> : "Terima"}</Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!history.length && <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">Belum ada transfer</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
